/**
 * Profile photos, read from Microsoft Graph.
 *
 * @module platform/graph-photo
 */

import { type AuthenticationProvider, Client } from "@microsoft/microsoft-graph-client";

/** Photos already fetched, so a roster that re-renders does not refetch. */
const photos = new Map<string, Promise<string | null>>();

/** The one client attempt this session made, granted or refused. */
let client: Promise<Client | null> | null = null;

/**
 * A Graph client, or nothing where this tenant has not allowed one.
 *
 * Attempted once per session and remembered either way. `User.ReadBasic.All`
 * is optional: the registration is multi-tenant, so each customer's admin
 * decides, and MSAL issues no token for a scope nobody consented to. A refusal
 * is therefore the scope check, and repeating it once per peer would be a
 * silent interaction prompt per avatar.
 *
 * The SDK's own extension point is used rather than the bundled MSAL bridge,
 * which targets msal-browser v2 and v3 where this app is on v5.
 *
 * @param getToken - Acquires a token carrying the photo scope.
 * @returns A client, or null where the scope is not granted.
 */
export function graphClientFor(getToken: () => Promise<string>): Promise<Client | null> {
  client ??= getToken()
    .then((): Client | null => {
      const authProvider: AuthenticationProvider = { getAccessToken: getToken };
      return Client.initWithMiddleware({ authProvider });
    })
    .catch(() => null);
  return client;
}

/**
 * Whether an id could belong to a Microsoft-issued account.
 *
 * Seeded users carry a plain string, `erika` or `professor-oak`, which
 * `src/db/seed/types.ts` guarantees no real `oid` claim will ever equal.
 * Asking Graph about them is a round trip that can only 404, once per peer per
 * roster, against a tenant where they do not exist.
 */
function isDirectoryId(userId: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userId);
}

/**
 * One person's photo as an object URL, or nothing.
 *
 * Nothing is the ordinary case, not a failure: most people never set a photo,
 * and without admin consent for the scope every call refuses. Both land on the
 * initials the avatar falls back to, so neither is worth surfacing.
 *
 * @param getToken - Acquires a token carrying the photo scope.
 * @param userId - The user to read.
 * @returns An object URL, or null when there is no photo to show.
 */
export async function profilePhotoUrl(
  getToken: () => Promise<string>,
  userId: string,
): Promise<string | null> {
  if (!isDirectoryId(userId)) {
    return null;
  }
  const graph = await graphClientFor(getToken);
  if (!graph) {
    return null;
  }
  const cached = photos.get(userId);
  if (cached) {
    return cached;
  }
  const pending = graph
    .api(`/users/${userId}/photo/$value`)
    .get()
    .then((blob: Blob) => URL.createObjectURL(blob))
    .catch(() => null);
  photos.set(userId, pending);
  return pending;
}

/** Forget every fetched photo, releasing the object URLs behind them. */
export async function forgetProfilePhotos(): Promise<void> {
  const urls = await Promise.all([...photos.values()]);
  urls.forEach((url) => {
    if (url) {
      URL.revokeObjectURL(url);
    }
  });
  photos.clear();
  client = null;
}
